import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const LyrikaPrivacyPage = () => {
  return (
    <div className="text-gray-100 font-sans relative selection:bg-fuchsia-500/30">
      <SEO
        title="Privacy Policy — Lyrika-Stanza Discord Bot"
        description="Official Privacy Policy for Lyrika-Stanza Discord bot, outlining data storage compliance and security safeguards."
        path="/lyrika/privacy"
        keywords={['Lyrika Privacy Policy', 'Lyrika-Stanza Privacy', 'Discord Bot Data Policy', 'Koneko']}
      />
      <div className="max-w-4xl mx-auto relative z-10 space-y-8">
        {/* Navigation / Header */}
        <div className="flex items-center justify-between border-b border-purple-500/20 pb-6">
          <Link 
            to="/projects/discordbots/lyrika-stanza" 
            className="inline-flex items-center gap-2 text-sm font-mono text-gray-400 hover:text-fuchsia-300 transition-colors group"
          >
            <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Lyrika-Stanza Homepage
          </Link>
          <div className="flex items-center gap-2">
            <img src="/lyrika-avatar.webp" alt="Lyrika-Stanza" className="w-8 h-8 rounded-full border border-purple-500/40" />
            <span className="text-sm font-serif font-bold text-white">Lyrika-Stanza Legal</span>
          </div>
        </div>

        {/* Legal Document Container */}
        <div className="bg-[#090714] border border-purple-500/20 rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-2xl space-y-6 sm:space-y-8">
          <div className="space-y-2 border-b border-purple-500/20 pb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950/60 border border-purple-500/40 text-fuchsia-300 text-[11px] sm:text-xs font-mono">
              <span>🔒</span> Official Privacy Compliance for Discord App Verification
            </div>
            <h1 className="text-3xl sm:text-4xl font-serif text-white tracking-wide">Privacy Policy</h1>
            <p className="text-xs font-mono text-gray-400">Effective Date: June 27, 2026 • Host Infrastructure: Wispbyte Node Network</p>
          </div>

          <div className="prose prose-invert max-w-none text-gray-300 space-y-6 text-sm leading-relaxed font-sans">
            <p className="font-serif italic">
              — This Privacy Policy explains how <strong className="text-fuchsia-300 font-sans">Lyrika-Stanza</strong> (&quot;we&quot;, &quot;our&quot;, or &quot;the bot&quot;) collects, uses, stores, and protects data when installed in your Discord server. We are committed to maintaining strict user privacy and collecting only the operational minimum necessary to render synchronized lyrics.
            </p>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">1.</span> Information We Collect &amp; Store
              </h2>
              <p>Lyrika-Stanza stores specific server configuration parameters in a dedicated SQLite database hosted on Wispbyte node servers. <strong className="text-white">We do not sell, rent, or trade your data to any third parties.</strong> We do not collect personal user profile data, IP addresses, or personal messages. The stored parameters include:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-gray-300 font-mono text-xs">
                <li><strong className="text-white">guild_id:</strong> Discord Guild Snowflake ID for server identification.</li>
                <li><strong className="text-white">listen_channel_id:</strong> Snowflake ID of the assigned music channel.</li>
                <li><strong className="text-white">output_channel_id:</strong> Snowflake ID of the assigned lyrics display channel.</li>
                <li><strong className="text-white">target_bot_id:</strong> Snowflake ID of the target music bot user.</li>
                <li><strong className="text-white">sync_offset_ms:</strong> Server timing calibration in milliseconds.</li>
              </ul>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">2.</span> Message Content &amp; Intent Processing
              </h2>
              <p>To detect playing music, Lyrika-Stanza requests the privileged <code className="text-purple-300">MessageContent</code> intent. The processing rules strictly adhere to Discord Developer policy:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-gray-300">
                <li><strong>Ephemeral Processing:</strong> Messages are inspected strictly within channels configured by administrators via slash commands.</li>
                <li><strong>Track Parsing Only:</strong> The bot parses message embeds sent by the designated target music bot to resolve song title and artist queries.</li>
                <li><strong>No User Logs:</strong> General user chat messages, attachments, and personal user data are ignored completely and are never written to disk or stored in any database.</li>
              </ul>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">3.</span> Discord Scopes &amp; Permissions Breakdown
              </h2>
              <p>The following OAuth2 scopes and permissions are requested specifically for bot operation and slash command registration:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 font-mono text-xs">
                <div className="bg-[#090714] p-3 rounded-xl border border-purple-500/20">
                  <span className="text-fuchsia-400 font-bold block mb-1">OAuth2 Scopes:</span>
                  <ul className="list-disc pl-4 space-y-1 text-gray-300">
                    <li><code className="text-purple-300">applications.commands</code></li>
                    <li><code className="text-purple-300">bot</code></li>
                  </ul>
                </div>
                <div className="bg-[#090714] p-3 rounded-xl border border-purple-500/20">
                  <span className="text-pink-400 font-bold block mb-1">Required Permissions:</span>
                  <ul className="list-disc pl-4 space-y-1 text-gray-300">
                    <li>Add Reactions</li>
                    <li>Embed Links</li>
                    <li>Manage Messages</li>
                    <li>Read Message History</li>
                    <li>Send Messages</li>
                    <li>View Channels</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">4.</span> Third-Party Data Transmission
              </h2>
              <p>Song search strings (e.g. artist name and song title) are transmitted via encrypted HTTPS requests to <code className="text-fuchsia-400">lrclib.net</code> to fetch synchronized lyrics. We do not transmit Discord server IDs, user IDs, or any identifiable metadata to external third parties.</p>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">5.</span> Data Retention &amp; Deletion Policy
              </h2>
              <p>Server configuration records are retained while the bot is installed in your server. Administrators can request or trigger data removal at any time by:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-gray-300">
                <li>Removing or kicking Lyrika-Stanza from the server.</li>
                <li>Executing slash command updates to overwrite stored channel routing parameters.</li>
              </ul>
            </section>

            <section className="space-y-3 bg-[#0d0a1c] p-5 rounded-2xl border border-purple-500/20">
              <h2 className="text-lg font-serif font-bold text-white flex items-center gap-2 border-b border-purple-500/20 pb-2">
                <span className="text-fuchsia-400 font-mono">6.</span> Contact &amp; Support Inquiries
              </h2>
              <p>For any privacy inquiries, data deletion requests, or support questions regarding Lyrika-Stanza, please reach out to <a href="https://x.com/NottKoneko" target="_blank" rel="noopener noreferrer" className="text-fuchsia-400 hover:text-fuchsia-300 underline font-semibold">@NottKoneko on X</a> or submit an issue on our official GitHub repository.</p>
            </section>
          </div>

          {/* Footer Navigation link */}
          <div className="pt-6 border-t border-purple-500/20 flex justify-between items-center text-xs font-mono">
            <Link to="/projects/discordbots/lyrika-stanza/tos" className="text-fuchsia-400 hover:text-fuchsia-300 hover:underline">
              ← View Terms of Service
            </Link>
            <span className="text-gray-400">Lyrika-Stanza Legal Compliance</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LyrikaPrivacyPage;
